import React from 'react';
import { EMOJI_MAP, EMOTION_COLORS, ENERGY_EMOJI, ENERGY_COLORS, ENERGY_SCORE } from '../utils/constants';

const Card = ({ children, style }) => (
  <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, padding:'16px 18px', ...style }}>
    {children}
  </div>
);
const Label = ({ children }) => (
  <div style={{ fontSize:'0.62rem', letterSpacing:'0.13em', textTransform:'uppercase', color:'var(--muted)', marginBottom:8 }}>
    {children}
  </div>
);
const BigVal = ({ children, color }) => (
  <div style={{ fontFamily:"'Fraunces',serif", fontSize:'clamp(1.1rem,2.2vw,1.6rem)', color: color||'var(--text)', lineHeight:1.1 }}>
    {children}
  </div>
);

// ── Attention arc ─────────────────────────────────────────────────────────────
function AttentionArc({ score }) {
  const r = 36, cx = 44, cy = 44, stroke = 6;
  const circ = 2 * Math.PI * r;
  const pct  = (score ?? 0) / 100;
  const col  = score >= 70 ? '#0cb8a0' : score >= 40 ? '#f0c060' : '#ef4444';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:16 }}>
      <svg width={88} height={88}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--surface2)" strokeWidth={stroke} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={col} strokeWidth={stroke}
          strokeDasharray={`${circ * pct} ${circ}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
        <text x={cx} y={cy+5} textAnchor="middle" fill={col} fontSize="16" fontFamily="'Fraunces',serif" fontWeight="bold">
          {score ?? '—'}
        </text>
        <text x={cx} y={cy+20} textAnchor="middle" fill="var(--muted)" fontSize="8" fontFamily="'Nunito',sans-serif">
          / 100
        </text>
      </svg>
      <div>
        <Label>Attention Score</Label>
        <div style={{ fontSize:'0.72rem', color: col, marginBottom:4 }}>
          {score === null ? 'Calibrating…' : score >= 70 ? '🎯 Focused' : score >= 40 ? '⚠ Distracted' : '😴 Low Attention'}
        </div>
        <div style={{ fontSize:'0.62rem', color:'var(--muted)' }}>
          Combined focus metric
        </div>
      </div>
    </div>
  );
}

// ── Emotion card ──────────────────────────────────────────────────────────────
export function EmotionCard({ emotion, expressions }) {
  const ec = EMOTION_COLORS[emotion] || 'var(--muted)';
  const top5 = expressions
    ? Object.entries(expressions).sort((a,b) => b[1]-a[1]).slice(0,5)
    : [];
  // Always pad to 5 rows so card height never changes
  const EMOTIONS_ORDER = ['neutral','happy','sad','surprised','angry'];
  const displayRows = EMOTIONS_ORDER.map(e => {
    const found = top5.find(([k]) => k === e);
    return found ? found : [e, 0];
  });

  return (
    <Card style={{ height:'100%', boxSizing:'border-box' }}>
      <Label>Detected Emotion</Label>
      <BigVal color={ec}>
        {emotion ? EMOJI_MAP[emotion] : '—'}&nbsp;
        <span style={{ textTransform:'capitalize' }}>{emotion || 'Waiting…'}</span>
      </BigVal>
      <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:5 }}>
        {displayRows.map(([e, v]) => (
          <div key={e} style={{ display:'grid', gridTemplateColumns:'20px 1fr 36px 28px', alignItems:'center', gap:5, fontSize:'0.63rem' }}>
            <span>{EMOJI_MAP[e]||'😐'}</span>
            <div style={{ height:3, background:'var(--surface2)', borderRadius:2, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${(v*100).toFixed(0)}%`, background: EMOTION_COLORS[e]||'var(--border)', borderRadius:2, transition:'width 0.4s' }} />
            </div>
            <span style={{ color:'var(--muted)', textTransform:'capitalize', fontSize:'0.58rem' }}>{e}</span>
            <span style={{ textAlign:'right', color:'var(--muted)' }}>{(v*100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Energy + blink card ───────────────────────────────────────────────────────
export function EnergyCard({ energy, ear, blinkRate, blinkCount, blinkDurationAvg, calibrated, calibProgress }) {
  const enCol = ENERGY_COLORS[energy] || 'var(--muted)';
  const score = ENERGY_SCORE[energy] || 0;
  return (
    <Card style={{ height:'100%', boxSizing:'border-box' }}>
      <Label>Energy / Alertness</Label>
      {/* Calibration bar — only shown during calibration, uses height:0 when done so no gap */}
      <div style={{ overflow:'hidden', maxHeight: calibrated ? 0 : 56, transition:'max-height 0.4s ease', marginBottom: calibrated ? 0 : 8 }}>
        <div style={{ fontSize:'0.62rem', color:'#f0a040', background:'#f0a04012', border:'1px solid #f0a04035', borderRadius:6, padding:'5px 9px', display:'flex', gap:6, alignItems:'center' }}>
          <span>⚙</span>
          <div style={{ flex:1 }}>
            Calibrating… {calibProgress ?? 0}%
            <div style={{ height:3, background:'#f0a04030', borderRadius:2, marginTop:3, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${calibProgress??0}%`, background:'#f0a040', borderRadius:2, transition:'width 0.4s' }} />
            </div>
          </div>
        </div>
      </div>
      <BigVal color={enCol}>
        {energy ? ENERGY_EMOJI[energy] : '—'}&nbsp;
        <span style={{ textTransform:'capitalize' }}>{energy || 'Waiting…'}</span>
      </BigVal>
      <div style={{ marginTop:10 }}>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.62rem', color:'var(--muted)', marginBottom:3 }}>
          <span>Level</span><span style={{ color:enCol }}>{score}%</span>
        </div>
        <div style={{ height:6, background:'var(--surface2)', borderRadius:3, overflow:'hidden' }}>
          <div style={{ height:'100%', width:`${score}%`, background:enCol, borderRadius:3, transition:'width 0.6s ease, background 0.6s ease' }} />
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginTop:12 }}>
        {[
          { label:'Blinks', val: blinkCount ?? 0, color:'var(--accent)' },
          { label:'/min', val: blinkRate !== null ? blinkRate?.toFixed(1) : '—', color:'var(--accent2)' },
          { label:'ms/blink', val: blinkDurationAvg || '—', color:'#f0d060' },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ background:'var(--surface2)', borderRadius:8, padding:'8px 4px', textAlign:'center', minWidth:0, overflow:'hidden' }}>
            <div style={{ fontFamily:"'Fraunces',serif", fontSize:'1.25rem', color, lineHeight:1, fontVariantNumeric:'tabular-nums', height:'1.4rem', overflow:'hidden' }}>{val}</div>
            <div style={{ fontSize:'0.5rem', color:'var(--muted)', marginTop:3, textTransform:'uppercase', letterSpacing:'0.04em', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{label}</div>
          </div>
        ))}
      </div>

      {ear !== null && (
        <div style={{ marginTop:8, fontSize:'0.6rem', color:'var(--muted)', display:'flex', gap:10 }}>
          <span>EAR {ear?.toFixed(3)}</span>
          {calibrated && <span style={{ color:'#60f0a4' }}>✓ calibrated</span>}
        </div>
      )}
    </Card>
  );
}

// ── Attention card ────────────────────────────────────────────────────────────
export function AttentionCard({ attentionScore, perclos, gazeOnScreen, headPose, yawnCount }) {
  return (
    <Card>
      <AttentionArc score={attentionScore} />
      <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:6 }}>
        {[
          { label:'PERCLOS', val: perclos !== null ? `${(perclos*100).toFixed(1)}%` : '—', alert: perclos > 0.10 },
          { label:'Gaze', val: gazeOnScreen === null ? '—' : gazeOnScreen ? '🖥 On screen' : '👀 Away', alert: gazeOnScreen === false },
          { label:'Head Yaw', val: headPose ? `${(headPose.yaw*100).toFixed(0)}°` : '—', alert: headPose && Math.abs(headPose.yaw) > 0.3 },
          { label:'Yawns', val: yawnCount ?? 0, alert: yawnCount > 3 },
        ].map(({ label, val, alert }) => (
          <div key={label} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.67rem' }}>
            <span style={{ color:'var(--muted)' }}>{label}</span>
            <span style={{ color: alert ? '#f08040' : 'var(--text)' }}>{val}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Focus tracker ─────────────────────────────────────────────────────────────
export function FocusCard({ focusState, focusPct, focusedMs, distractedMs }) {
  const fmtMs = ms => {
    if (!ms || ms < 1000) return '0m';
    const m = Math.floor(ms/60000), s = Math.floor((ms%60000)/1000);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };
  return (
    <Card>
      <Label>Focus Tracker</Label>
      {focusState === null ? (
        <div style={{ fontSize:'0.72rem', color:'var(--muted)', padding:'8px 0' }}>Start tracking to measure focus</div>
      ) : (
        <>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background: focusState==='focused'?'var(--energetic)':'var(--angry)', boxShadow:`0 0 8px ${focusState==='focused'?'var(--energetic)':'var(--angry)'}`, animation:'pulse 2s infinite' }} />
            <span style={{ fontSize:'0.78rem', color: focusState==='focused'?'var(--energetic)':'var(--angry)' }}>
              {focusState === 'focused' ? '🎯 Focused' : '👀 Distracted'}
            </span>
          </div>
          {focusPct !== null && (
            <div style={{ marginBottom:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.62rem', color:'var(--muted)', marginBottom:4 }}>
                <span>Focus score</span>
                <span style={{ color: focusPct>=70?'var(--energetic)':focusPct>=40?'var(--accent)':'var(--angry)' }}>{focusPct}%</span>
              </div>
              <div style={{ height:8, background:'var(--surface2)', borderRadius:4, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${focusPct}%`, background: focusPct>=70?'var(--energetic)':focusPct>=40?'var(--accent)':'var(--angry)', borderRadius:4, transition:'width 1s ease' }} />
              </div>
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {[{ label:'Focused', val:fmtMs(focusedMs), color:'var(--energetic)' }, { label:'Distracted', val:fmtMs(distractedMs), color:'var(--angry)' }].map(({ label, val, color }) => (
              <div key={label} style={{ background:'var(--surface2)', borderRadius:8, padding:'8px 10px', textAlign:'center', minWidth:0 }}>
                <div style={{ fontSize:'0.9rem', color, fontWeight:600 }}>{val}</div>
                <div style={{ fontSize:'0.55rem', color:'var(--muted)', marginTop:2, textTransform:'uppercase' }}>{label}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
